import { FC, memo, useCallback, useEffect, useState } from 'react';

import { CompanyTagsProvider, useCompanyTagsContext } from 'application/components/common';
import { withProviders } from 'application/helpers';
import usePromoState from 'application/hooks/usePromoState';
import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import PlayerProvider from 'application/providers/PlayerProvider';
import { useAppDispatch, useAppSelector } from '@shared/store';

import { AdCampaignMediaTypeEnum } from '@shared/api/graphql/generated';

import {
  selectStep,
  selectSelectedTags,
  selectMediaType,
  selectPromoStatuses,
  selectPromoTimesById,
} from '../../slices/createAdCampaign/selectors';
import { CREATE_AD_CAMPAIGN_STEPS } from '../../slices/createAdCampaign/consts';
import {
  resetCreateAdCampaign,
  setStep,
  setSelectedTags,
  setPromoStatuses,
  setPromoTimesById,
} from '../../slices/createAdCampaign/slice';
import { getStorablePromoStatuses } from '../../slices/createAdCampaign/utils';
import Media from './Media';
import Points from './Points';
import Settings from './Settings';

export const CreateAdCampaignModal: FC<ModalProps[ModalTypes.AD_CAMPAIGN_STEPPER_CREATE]> = memo(
  ({ close, replaceModal, canBackToAdCampaignTypeSelect, canBackToPromoRollSynth }) => {
    const dispatch = useAppDispatch();
    const currentStep = useAppSelector(selectStep);
    const storedTags = useAppSelector(selectSelectedTags);
    const storedPromoStatuses = useAppSelector(selectPromoStatuses);
    const storedPromoTimesById = useAppSelector(selectPromoTimesById);
    const mediaType = useAppSelector(selectMediaType) ?? AdCampaignMediaTypeEnum.Audio;
    const promoState = usePromoState(storedPromoStatuses);
    const promoStatuses = promoState.data.rawStatuses;
    const { initTags, getSelectedTagsSnapshot } = useCompanyTagsContext();

    const [timeById, setTimeById] = useState<Record<string, string>>(() => storedPromoTimesById);

    useEffect(() => {
      if (storedTags.length > 0) {
        initTags(storedTags);
      }
    }, []);

    useEffect(() => {
      dispatch(setPromoStatuses(getStorablePromoStatuses(promoStatuses)));
    }, [dispatch, promoStatuses]);

    useEffect(() => {
      dispatch(setPromoTimesById(timeById));
    }, [dispatch, timeById]);

    const handleClose = useCallback(() => {
      dispatch(resetCreateAdCampaign());
      promoState.clear();
      close();
    }, [dispatch, close, promoState]);

    const handleSettingsNext = useCallback(() => {
      dispatch(setStep(CREATE_AD_CAMPAIGN_STEPS.MEDIA));
    }, [dispatch]);

    const handleSettingsBack = useCallback(() => {
      dispatch(setSelectedTags(getSelectedTagsSnapshot()));
      replaceModal(ModalTypes.SELECT_AD_CAMPAIGN_TYPE, {});
    }, [dispatch, getSelectedTagsSnapshot, replaceModal]);

    const handleSettingsBackToPromoRollSynth = useCallback(() => {
      replaceModal(ModalTypes.PROMO_ROLL_SYNTH, {});
    }, [replaceModal]);

    const getSettingsOnBack = () => {
      if (canBackToAdCampaignTypeSelect) return handleSettingsBack;
      if (canBackToPromoRollSynth) return handleSettingsBackToPromoRollSynth;
      return undefined;
    };

    const handleMediaBack = useCallback(() => {
      dispatch(setStep(CREATE_AD_CAMPAIGN_STEPS.SETTINGS));
    }, [dispatch]);

    const handleMediaNext = useCallback(() => {
      dispatch(setStep(CREATE_AD_CAMPAIGN_STEPS.POINTS));
    }, [dispatch]);

    const handlePointsBack = useCallback(() => {
      dispatch(setStep(CREATE_AD_CAMPAIGN_STEPS.MEDIA));
    }, [dispatch]);

    const handleTimeChange = useCallback((id: string, time: string) => {
      setTimeById((prev) => ({ ...prev, [id]: time }));
    }, []);

    const handleTimeRemove = useCallback((id: string) => {
      setTimeById((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }, []);

    const renderStepContent = () => {
      switch (currentStep) {
        case CREATE_AD_CAMPAIGN_STEPS.SETTINGS:
          return (
            <Settings
              mediaType={mediaType}
              close={handleClose}
              onBack={getSettingsOnBack()}
              onNext={handleSettingsNext}
            />
          );
        case CREATE_AD_CAMPAIGN_STEPS.MEDIA:
          return (
            <Media
              mediaType={mediaType}
              close={handleClose}
              onBack={handleMediaBack}
              onNext={handleMediaNext}
              promoState={promoState}
              timeById={timeById}
              onTimeChange={handleTimeChange}
              onTimeRemove={handleTimeRemove}
            />
          );
        case CREATE_AD_CAMPAIGN_STEPS.POINTS:
          return (
            <Points
              mediaType={mediaType}
              close={handleClose}
              onBack={handlePointsBack}
              promoState={promoState}
              timeById={timeById}
              canBackToPromoRollSynth={canBackToPromoRollSynth}
            />
          );
        default:
          return null;
      }
    };

    return <>{renderStepContent()}</>;
  }
);

export default withProviders(CreateAdCampaignModal, [PlayerProvider, CompanyTagsProvider]);
